import {Provider, Button} from '@clayui/core';
import React, {useEffect, useState} from 'react';
import icons from '@clayui/css/lib/images/icons/icons.svg';
import Form from "@clayui/form";
import Alert from '@clayui/alert';
import LoadingIndicator from "@clayui/loading-indicator";
import {FormInputText} from "./forms/inputs/FormInputText";
import {FormDropdownSelect} from "./forms/inputs/FormDropdownSelect";
import {useForm, FormProvider} from "react-hook-form";
import {FormInputDate} from "./forms/inputs/FormInputDate";
import {FormInputRadio} from "./forms/inputs/FormInputRadio";
import {FormInputZipCode} from "./forms/inputs/FormInputZipCode";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Voivodeship, District, Commune, City, Street, KdrFormValues, Submission, Category } from "../dto/dto";
import dayjs from "dayjs";
import {
    getCategories,
    getCities,
    getCommunes,
    getDistricts,
    getStreets,
    autocompleteStreets,
    getVoivodeships,
    submitForm
} from "../api/adressFields";
import logo from '../assets/kdr-form.png'
import FormDropdownAutocomplete from "./forms/inputs/FormDropdownAutomplete";
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../context/SubmissionContext';
import { scrollToFirstError } from '../utils/scrollToError';

export default function KdrForm() {
    const defaultValues: KdrFormValues = {
        declarationDate: null,
        name: '',
        regon: null,
        krs: null,
        website: null,
        branchCount: null,
        voivodeshipId: '',
        districtId: '',
        communeId: '',
        zipcode: '',
        cityId: '',
        streetId: null,
        buildingNumber: '',
        apartmentNumber: null,
        lastName: '',
        firstName: '',
        phoneNumber: '',
        email: null,
        categoryId: '',
        privilegePercent: null,
        privilegeDescription: '',
        validityStart: null,
        validityEnd: null,
        territorialRange: ''
    }
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [isLoading, setIsLoading] = useState(false);
    const [isDistrictLoading, setIsDistrictLoading] = useState(false);
    const [isCommuneLoading, setIsCommuneLoading] = useState(false);
    const [isCityLoading, setIsCityLoading] = useState(false);
    const [isStreetLoading, setIsStreetLoading] = useState(false);
    const navigate = useNavigate();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { submissionForm, setSubmitResponseValue } = useMyContext();

    const [voivodeshipOptions, setVoivodeshipOptions] = useState<Voivodeship[]>([]);
    const [districtOptions, setDistrictOptions] = useState<District[]>([]);
    const [communeOptions, setCommuneOptions] = useState<Commune[]>([]);
    const [cityOptions, setCityOptions] = useState<City[]>([]);
    const [streetOptions, setStreetOptions] = useState<Street[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
    const [toastItems, setToastItems] = useState<{type: string, text: string}[] | null>(null);
    const [wasAlreadySubmitted, setWasAlreadySubmitted] = useState(false);

    const schema: yup.ObjectSchema<any> = yup.object().shape({
        declarationDate: yup.string()
            .required('To pole jest wymagane')
            .test('is-valid-date', 'Nieprawidłowa data', (value) => {
                if (!value) return true;
                const date = new Date(value);

                return !isNaN(date.getTime());
            })
            .test(
                'is-today-or-before',
                'Data nie może być datą z przyszłości',
                value => {
                    if (!value) return true;
                    const today = new Date().toLocaleDateString('en-CA');
                    return value <= today;
                }
            ),
        name: yup.string()
            .required('To pole jest wymagane')
            .max(200, 'Ilość znaków nie może być większa niż 200'),
        regon: yup.string()
            .nullable()
            .test('regon-format', 'Nieprawidłowy format numeru REGON', value => {
                if (!value?.trim()) return true;
                return /^\d{9}(\d{5})?$/.test(value);
            }),
        krs: yup.string()
            .nullable()
            .test('krs-format', 'Nieprawidłowy format KRS', value => {
                if (!value?.trim()) return true;
                return /^\d{10}$/.test(value);
            }),
        website: yup.string()
            .nullable()
            .max(200, 'Ilość znaków nie może być większa niż 200'),
        branchCount: yup.number()
            .required('To pole jest wymagane')
            .transform((value, originalValue) => {
                return originalValue === '' ? null : value;
            })
            .integer('Liczba oddziałów musi być liczbą całkowitą')
            .min(1, 'Liczba oddziałów musi być większa od 0'),
        voivodeshipId: yup.string()
            .required('To pole jest wymagane'),
        districtId: yup.string()
            .required('To pole jest wymagane'),
        communeId: yup.string()
            .required('To pole jest wymagane'),
        zipcode: yup.string()
            .trim()
            .required('To pole jest wymagane')
            .matches(/^\d{2}-\d{3}$/, 'Kod pocztowy musi być w formacie XX-XXX'),
        cityId: yup.string()
            .required('To pole jest wymagane'),
        streetId: streetOptions && streetOptions.length > 0
            ? yup.string().required('To pole jest wymagane')
            : yup.string().notRequired(),
        buildingNumber: yup.string()
            .nullable()
            .max(20, 'Numer budynku nie może być większ niż 20')
            .required('To pole jest wymagane'),
        apartmentNumber: yup.string()
            .nullable()
            .max(20, 'Ilość znaków nie może być większa niż 20'),
        lastName: yup.string()
            .required('To pole jest wymagane')
            .max(200, 'Ilość znaków nie może być większa niż 200'),
        firstName: yup.string()
            .required('To pole jest wymagane')
            .max(200, 'Ilość znaków nie może być większa niż 200'),
        phoneNumber: yup.string()
            .required('To pole jest wymagane')
            .matches(/^\d{9}?$/, 'Nieprawidłowy format numeru telefonu'),
        email: yup.string()
            .required('To pole jest wymagane')
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/, 'Nieprawidłowy format adresu email'),
        categoryId: yup.string()
            .required('To pole jest wymagane'),
        privilegePercent: yup.number()
            .nullable()
            .transform((value, originalValue) => {
                return originalValue === '' ? null : value;
            })
            .min(1, 'Liczba musi być z przedziału 1-100')
            .max(100, 'Liczba musi być z przedziału 1-100')
            .test(
                'max-two-decimals',
                'Maksymalnie dwie cyfry po przecinku',
                (value) => {
                    if (value === undefined || value === null) return true;
                    return /^\d+(\.\d{1,2})?$/.test(value.toString());
                }),
        privilegeDescription: yup.string()
            .required('To pole jest wymagane')
            .max(300, 'Ilość znaków nie może być większa niż 300'),
        validityStart: yup.string()
            .required('To pole jest wymagane')
            .test('is-valid-date', 'Nieprawidłowa data', (value) => {
                if (!value) return true;
                const date = new Date(value);
                return !isNaN(date.getTime());
            }).test('is-after-declaration', 'Data obowiązywania uprawnienia nie może być wcześniejsza niż data złożenia deklaracji', function(value) {
                const { declarationDate } = this.parent;
                if (!value || !validityStart) return true;

                const startDate = new Date(declarationDate);
                const endDate = new Date(value);
                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

                return endDate >= startDate;
            }),
        validityEnd: yup.string()
            .nullable()
            .test('is-valid-date', 'Nieprawidłowa data', (value) => {
                if (!value) return true;
                const date = new Date(value);
                return !isNaN(date.getTime());
            })
            .test('is-after-start', 'Data końcowa nie może być wcześniejsza, niż data początkowa', function(value) {
                const { validityStart } = this.parent;
                if (!value || !validityStart) return true;

                const startDate = new Date(validityStart);
                const endDate = new Date(value);
                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

                return endDate >= startDate;
            }),
        territorialRange: yup.string()
            .required('To pole jest wymagane')
    }).test(
        'krs-or-regon-required',
        'Musisz wypełnić KRS lub REGON',
        function (value) {
            const { krs, regon } = value || {};

            if (krs?.trim() || regon?.trim()) {
                return true;
            }

            return new yup.ValidationError(
                [
                    this.createError({ path: 'krs', message: 'Musisz wypełnić KRS lub REGON' }),
                    this.createError({ path: 'regon', message: 'Musisz wypełnić KRS lub REGON' }),
                ],
                null,
                this.path
            );
        }
    );

    const form = useForm<KdrFormValues>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema),
    });
    const { trigger , watch } = form;
    const voivodeshipId = watch('voivodeshipId');
    const districtId = watch('districtId');
    const communeId = watch('communeId');
    const cityId = watch('cityId');
    const krs = watch('krs');
    const regon = watch('regon');
    const declarationDate = watch('declarationDate');
    const validityStart = watch('validityStart');
    const validityEnd = watch('validityEnd');

    useEffect(() => {
        (async () => {
            const voivodeships = await getVoivodeships();
            const categories = await getCategories();
            setVoivodeshipOptions(voivodeships);
            setCategoryOptions(categories);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        form.setValue('districtId', '');
        if (voivodeshipId === '') return;
        setIsDistrictLoading(true);
        setDistrictOptions([]);
        (async () => {
            const options = await getDistricts(voivodeshipId);
            setDistrictOptions(options);
            setIsDistrictLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voivodeshipId]);

    useEffect(() => {
        form.setValue('communeId', '');
        if (districtId === '') return;
        setIsCommuneLoading(true);
        setCommuneOptions([]);
        (async () => {
            const options = await getCommunes(districtId);
            setCommuneOptions(options);
            setIsCommuneLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [districtId]);

    useEffect(() => {
        form.setValue('cityId', '');
        if (communeId === '') return;
        setIsCityLoading(true);
        setCityOptions([]);
        (async () => {
            const options = await getCities(communeId);
            setCityOptions(options);
            setIsCityLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communeId]);

    useEffect(() => {
        form.setValue('streetId', '');
        if (cityId === '') return;
        setIsStreetLoading(true);
        setStreetOptions([]);
        (async () => {
            const options = await getStreets(cityId);
            setStreetOptions(options);
            setIsStreetLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityId]);

    useEffect(() => {
        if(wasAlreadySubmitted && (krs !== null || regon !== null)) {
            trigger(['regon', 'krs']);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regon, krs]);

    useEffect(() => {
        if(wasAlreadySubmitted) {
            trigger(['declarationDate', 'validityStart', 'validityEnd']);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationDate, validityStart, validityEnd]);

    const onChangeWithFocus = (elementId: string) => {
        const input = document.getElementById(elementId);
        if (!input) return;

        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>(
                'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
            )
        ).filter(el => el.offsetParent !== null);

        const index = focusable.indexOf(input);
        if (index < 0 || index >= focusable.length - 1) return;

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
            focusable[index + 1].focus();
            focusable[index + 1].click();
        }

        setTimeout(() => {
            const refreshedFocusable = Array.from(
                document.querySelectorAll<HTMLElement>(
                    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
                )
            ).filter(el => el.offsetParent !== null);

            const refreshedIndex = refreshedFocusable.indexOf(input);
            if (refreshedIndex < 0 || refreshedIndex >= refreshedFocusable.length - 1) return;

            refreshedFocusable[refreshedIndex + 1].focus();
        }, 100);
    };

    const onClearNull = (fieldName: string) => {
        form.setValue(fieldName as keyof KdrFormValues, null);
    }

    const onSubmit = async () => {
        try {
            if (!executeRecaptcha) return;
            setIsLoading(true);
            const token = await executeRecaptcha('submit_form');
            const formValues: KdrFormValues = form.getValues();
            //@ts-ignore
            const submitValues: Submission = {
                ...formValues,
                branchCount: formValues.branchCount ? formValues.branchCount.toString() : '',
                declarationDate: formValues.declarationDate ? dayjs(formValues.declarationDate, 'DD.MM.YYYY').format('YYYY-MM-DD') : '',
                validityStart: formValues.validityStart ? dayjs(formValues.validityStart, 'DD.MM.YYYY').format('YYYY-MM-DD') : '',
                validityEnd: formValues.validityEnd ? dayjs(formValues.validityEnd, 'DD.MM.YYYY').format('YYYY-MM-DD') : '',
                krs: formValues.krs ?? undefined,
                regon: formValues.regon ?? undefined,
                website: formValues.website ?? undefined,
                streetId: formValues.streetId ?? "",
                apartmentNumber: formValues.apartmentNumber ?? undefined,
                email: formValues.email ?? undefined,
                privilegePercent: formValues.privilegePercent?.toString() ?? undefined,
                captchaToken: token
            };
            const submitResponse = await submitForm(submitValues);
            setSubmitResponseValue(submitResponse);
            navigate(`${localStorage.getItem("kdr-form-pathname") + "?view=success"}`);
        } catch (error: any) {
            setIsLoading(false);
            const title = error?.response?.data?.title;
            const errorMessage = title && typeof title === 'string' && !title.includes("Couldn't create SOAP message")
                ? title
                : "Wysyłanie wniosku zakończone niepowodzeniem. Spróbuj ponownie.";
            setToastItems([{ type: 'danger', text: errorMessage }]);
        }
        setIsLoading(false);
    };

    return (
        <Provider spritemap={icons}>
            <FormProvider {...form}>
                <Alert.ToastContainer>
					{toastItems?.map((value) => (
						<Alert
							autoClose={5000}
							key={value.text + Math.random() * 10000}
                            displayType={value.type === 'success' ? 'success' : 'danger'}
                            title={value.type === 'success' ? 'Sukces' : 'Błąd'}
							onClose={() => {
								setToastItems((prevItems) =>
									prevItems ? prevItems.filter((item) => item.text !== value.text) : null
								);
							}}
						>
							{value.text}
						</Alert>
					))}
				</Alert.ToastContainer>
                <Form  {...( { noValidate: true } as any )} onSubmit={form.handleSubmit(onSubmit, (errors) => scrollToFirstError(errors))}>
                    <div className="header">
                        <img src={logo} alt="Logo" />
                        <h1>KARTA DUŻEJ RODZINY</h1>
                    </div>
                    <hr className="separator"/>
                    <h2>Dane Podmiotu</h2>
                    <div className="form-grid">
                        <FormInputDate label="Data złożenia deklaracji" name="declarationDate"
                                       onChangeWithFocus={(elementId: string) => onChangeWithFocus(elementId)} onClear={(fieldName: string) => onClearNull(fieldName)} required />
                        <FormInputText label="Nazwa" name="name" required />
                        <FormInputText label="REGON" name="regon" textNumberInput required={!krs} />
                        <FormInputText label="KRS" name="krs" textNumberInput required={!regon} />
                        <FormInputText label="Strona www" name="website" />
                        <FormInputText label="Liczba oddziałów" name="branchCount" textNumberInput required min={1} maxDigits={4}/>
                    </div>
                    <hr className="separator"/>
                    <h2>Dane adresowe siedziby Podmiotu</h2>
                    <div className="form-grid">
                        <FormDropdownSelect label="Województwo" name="voivodeshipId" placeholder="Wybierz województwo" options={voivodeshipOptions} required />
                        <FormDropdownSelect label="Powiat" name="districtId" placeholder="Wybierz powiat" options={districtOptions} isLoading={isDistrictLoading} required disabled={voivodeshipId === ''}/>
                        <FormDropdownSelect label="Gmina" name="communeId" placeholder="Wybierz gminę" options={communeOptions} isLoading={isCommuneLoading} required disabled={!districtId} />
                        <FormInputZipCode  label="Kod pocztowy" name="zipcode" required />
                        <div className="form-field full-width">
                            <FormDropdownSelect label="Miejscowość" name="cityId" placeholder="Wybierz miasto" options={cityOptions} isLoading={isCityLoading} required disabled={!communeId} />
                        </div>
                        <div className="form-field full-width">
                            <FormDropdownAutocomplete
                                label="Ulica"
                                name="streetId"
                                placeholder="Wybierz ulicę"
                                cityId={cityId}
                                options={streetOptions}
                                autocompleteStreets={autocompleteStreets}
                                required={!!cityId && streetOptions && streetOptions.length > 0}
                                disabled={!cityId || (!isStreetLoading && (!streetOptions || streetOptions.length === 0))}
                                onChangeWithFocus={(elementId: string) => onChangeWithFocus(elementId)}
                                isLoading={isStreetLoading}
                            />
                        </div>
                        <FormInputText label="Nr budynku" name="buildingNumber" required />
                        <FormInputText label="Nr lokalu" name="apartmentNumber" />
                    </div>
                    <hr className="separator"/>
                    <h2>Dane osoby do kontaktu</h2>
                    <div className="form-grid">
                        <FormInputText label="Nazwisko" name="firstName" required />
                        <FormInputText label="Imię" name="lastName" required />
                        <FormInputText label="Nr telefonu" name="phoneNumber" textNumberInput required />
                        <FormInputText label="E-mail" name="email" required />
                    </div>
                    <hr className="separator"/>
                    <h2>Dane proponowanego uprawnienia</h2>
                    <div className="form-grid">
                        <div  className="form-field full-width">
                            <FormDropdownSelect label="Kategorie/podkategorie" name="categoryId" placeholder="Wybierz kategorię" options={categoryOptions} required />
                        </div>
                        <FormInputText label="Rodzaj uprawnienia - wielkość w %" name="privilegePercent" textNumberInput min={1} maxDigits={5} allowDecimal />
                        <div  className="form-field full-width">
                            <FormInputText label="Rodzaj uprawnienia - opis" name="privilegeDescription" type="textarea" required />
                        </div>
                        <FormInputDate label="Termin obowiązywania uprawnienia od" name="validityStart" onChangeWithFocus={(elementId: string) => onChangeWithFocus(elementId)}
                                       onClear={(fieldName: string) => onClearNull(fieldName)} required />
                        <FormInputDate label="Termin obowiązywania uprawnienia do" name="validityEnd" onChangeWithFocus={(elementId: string) => onChangeWithFocus(elementId)}
                                       onClear={(fieldName: string) => onClearNull(fieldName)}/>
                        <div  className="form-field">
                            <small className="form-info">Należy wpisać datę lub pozostawić pole puste w przypadku decyzji o obowiązywaniu uprawnienia na czas nieokreślony</small>
                        </div>
                        <div  className="form-field full-width">
                            <FormInputRadio
                                name="territorialRange"
                                label="Zasięg terytorialny"
                                options={[
                                    { label: "ogólnopolski", value: "1" },
                                    { label: "regionalny (obejmuje kilka województw)", value: "2" },
                                    { label: "lokalny", value: "3" },
                                ]}
                                required
                            />
                        </div>
                    </div>
                    <div style={{'display': 'flex', 'gap': '1rem'}}>
                        <Button
                            disabled={isLoading}
                            type='submit'
                            onClick={() => setWasAlreadySubmitted(true)}
                            displayType="primary">
                            Wyślij
                        </Button>
                        {isLoading && (
                            <div className="mr-2" style={{ display: "flex", alignItems: "center" }}>
                                <LoadingIndicator displayType="secondary" size="sm"/>
                            </div>
                        )}
                    </div>
                </Form>
            </FormProvider>
        </Provider>
    )
}